import React, { useEffect, useRef } from 'react';
import Input from "../../../../components/Input";
import Select from "../../../../components/Select";
import Checkbox from "../../../../components/Checkbox";
import ApiServiceV2 from "../../../../services/ApiServiceV2";
import {routes} from "../../../../hooks/routes";
import cx from 'classnames';
import styles from './AutoInjectTab.module.scss';
import { ContactFormConfig } from '../../../../types/ContactForm';

interface AutoInjectTabProps {
    config: ContactFormConfig;
    updateConfig: (property: string, value: any) => void;
    shopUrl: string;
    setShopUrl: (url: string) => void;
}

const AutoInjectTab: React.FC<AutoInjectTabProps> = ({ 
    config,
    updateConfig,
    shopUrl,
    setShopUrl
}) => {
    const hasLoadedInitialSettings = useRef(false);

    const injectionPageOptions = [
        { value: 'all', label: 'Alle Seiten' },
        { value: 'homepage', label: 'Startseite' },
        { value: 'product', label: 'Produktseiten' },
        { value: 'category', label: 'Kategorieseiten' },
        { value: 'contact', label: 'Kontaktseite' },
        { value: 'cart', label: 'Warenkorb' },
        { value: 'checkout', label: 'Bestellvorgang' }
    ];

    const injectionPositionOptions = [
        { value: 'end', label: 'Am Ende des Elements einfügen' },
        { value: 'beginning', label: 'Am Anfang des Elements einfügen' },
        { value: 'after', label: 'Nach dem Element einfügen' },
        { value: 'before', label: 'Vor dem Element einfügen' },
        { value: 'html', label: 'Element-Inhalt ersetzen' }
    ];

    useEffect(() => {
        const loadShopUrl = async () => {
            try {
                const api = new ApiServiceV2();
                const response = await api.callApi(routes.server);
                if (response.data?.data?.url) {
                    setShopUrl(response.data.data.url);
                } else {
                    console.log('Shop URL not found in response data');
                }
            } catch (error) {
                console.error('Failed to load shop URL:', error);
            }
        };
        
        loadShopUrl();
    }, [setShopUrl]);

    // Get current injection settings from form config with defaults
    const autoInjectEnabled = config.autoInjectEnabled ?? false;
    const injectionSelector = config.injectionSelector ?? '#content-wrapper';
    const injectionPages = config.injectionPages ?? 'all';
    const injectionPosition = config.injectionPosition ?? 'end';

    return (
        <div className={cx(styles.container)}>
            <div className={cx(styles['info-box'], 'zr-mb-3')}>
                <p>
                    Mit der automatischen Einbindung wird dieses Kontaktformular automatisch auf den gewählten Seiten 
                    in das angegebene Element injiziert - ohne Template-Änderungen! 
                    <strong> Jedes Formular kann eigene Injection-Einstellungen haben.</strong>
                </p>
            </div>
            
            <div className="zr-mb-2">
                <Checkbox
                    property="autoInjectEnabled"
                    value={autoInjectEnabled}
                    onChange={(_, value) => updateConfig('autoInjectEnabled', value)}
                    label="Automatische Einbindung für dieses Formular aktivieren"
                />
            </div>
            
            {autoInjectEnabled && (
                <div className={cx('zr-mt-2', 'zr-pt-3')}>
                    <div className="zr-mb-3">
                        <Input
                            type="text"
                            property="injectionSelector"
                            value={injectionSelector}
                            onChange={(_, value) => updateConfig('injectionSelector', value)}
                            placeholder="CSS-Selektor (z.B. #content-wrapper, .main-content)"
                        />
                    </div>
                    
                    <div className="zr-mb-4">
                        <Select
                            property="injectionPages"
                            value={injectionPages}
                            onSelect={(_, value) => updateConfig('injectionPages', value)}
                            placeholder="Auf welchen Seiten einbinden?"
                            options={injectionPageOptions}
                            searchable={false}
                        />
                    </div>
                             
                    <div className="zr-mb-4">
                        <Select
                            property="injectionPosition"
                            value={injectionPosition}
                            onSelect={(_, value) => updateConfig('injectionPosition', value)}
                            placeholder="Position der Einbindung"
                            options={injectionPositionOptions}
                            searchable={false}
                        />
                    </div>
                    
                    <div className={cx(styles['tips-box'], 'zr-mb-4')}>
                        <h5>Häufig verwendete Selektoren:</h5>
                        <ul>
                            <li><code>#content-wrapper</code> - Haupt-Inhaltsbereich</li>
                            <li><code>#results_wrapper</code> - Ergebnisbereich</li>
                            <li><code>.main-content</code> - Hauptinhalt (falls vorhanden)</li>
                            <li><code>#product-info</code> - Produktinformationen</li>
                            <li><code>.container</code> - Bootstrap Container</li>
                        </ul>
                    </div>
                    
                    <div className={cx(styles['current-settings-box'], 'zr-mb-4')}>
                        <h5>Aktuelle Einstellung für dieses Formular:</h5>
                        <p>
                            Das Formular wird automatisch in <code>{injectionSelector}</code> 
                            auf <strong>{injectionPageOptions.find(p => p.value === injectionPages)?.label}</strong> eingefügt.
                        </p>
                        <p>
                            Position: <strong>{injectionPositionOptions.find(p => p.value === injectionPosition)?.label}</strong>
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AutoInjectTab; 